import React, { useEffect, useMemo, useState } from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';

import {
  WEEK_LIMIT_COUNT,
  ALLOCATION_TYPE_BOOKING,
  ALLOCATION_TYPE_CURRENT,
  PERSISTING_CANDIDATE_BOOKING_STATUSES,
} from 'constants/bookingAllocation';
import { dashboardColors } from 'constants/chartColors';
import { EMPTY_ALLOCATION_CHART_MESSAGE } from 'constants/dashboard';
import { ALLOCATION_PILL_TAB_OPTIONS } from 'constants/appConstants';

import { fetchUserBookingAllocation, fetchUserCurrentAllocations } from 'services/user';

import { getRandomColor, pluralize, startCase } from 'utils/string';
import { isDateToday, isDateInFuture, getFormattedDate, getWeeksLaterDate } from 'utils/date';
import type { AllocationItem } from 'interface/allocation';

import PillTab from 'components/common/pillTab';
import CommonChart from 'components/common/piechart/CommonChart';
import BookingAllocation, { BookedAllocation } from './components/BookingAllocation';

type Area = {
  name?: string;
};

type Designation = {
  area?: Area;
};

type ProjectAllocation = AllocationItem;

type BookingStatus = NonNullable<BookedAllocation['bookingStatus']> & {
  statusUpdatedAt?: string;
};

type BookingAllocationData = BookedAllocation & {
  bookingStatus: BookingStatus;
};

type ChartDatum = {
  key: number;
  label: string;
  value: number;
  color: string;
};

type AllocationAndBookingProps = {
  userId: number | string;
  designation?: Designation;
};

function AllocationAndBooking({ userId }: AllocationAndBookingProps) {
  const [isError, setIsError] = useState(false);
  const [colors, setColors] = useState(dashboardColors);
  const [currentAllocationLoading, setCurrentAllocationLoading] = useState(false);
  const [bookingAllocationLoading, setBookingAllocationLoading] = useState(false);
  const [projectAllocations, setProjectAllocations] = useState<ProjectAllocation[]>([]);
  const [bookedAllocation, setBookedAllocation] = useState<BookingAllocationData | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setCurrentAllocationLoading(true);

        const data = (await fetchUserCurrentAllocations(userId)) as ProjectAllocation[] | null;

        const currentAllocations = !data
          ? []
          : [...data]
              .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
              .filter((allocation) =>
                allocation.endDate ? isDateToday(allocation.endDate) || isDateInFuture(allocation.endDate) : true
              );

        setProjectAllocations(currentAllocations);

        if (currentAllocations.length > colors.length) {
          const totalColorsToBeAdded = currentAllocations.length - dashboardColors.length;

          const colorsToBeAdded = Array.from({ length: totalColorsToBeAdded + 1 }, getRandomColor);

          setColors((previousColor) => [...previousColor, ...colorsToBeAdded]);
        }
      } catch {
        setIsError(true);
      } finally {
        setCurrentAllocationLoading(false);
      }
    })();
    // Reads `colors.length` but must not re-run when we append colors after fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when userId only
  }, [userId]);

  useEffect(() => {
    (async () => {
      try {
        setBookingAllocationLoading(true);

        const data = (await fetchUserBookingAllocation(userId)) as BookingAllocationData | null;

        if (!data) {
          return;
        }

        const limitDate = getWeeksLaterDate(data.bookingStatus.statusUpdatedAt || data.targetDate, WEEK_LIMIT_COUNT);

        const includesPersistingStatus = PERSISTING_CANDIDATE_BOOKING_STATUSES.includes(data.bookingStatus.name);

        if (includesPersistingStatus && getFormattedDate() > limitDate) {
          return;
        }

        setBookedAllocation(data);
      } catch {
        // Booking allocation failures should not block current allocation rendering.
        void 0;
      } finally {
        setBookingAllocationLoading(false);
      }
    })();
  }, [userId]);

  const allocationSourceKey = `${currentAllocationLoading}:${bookingAllocationLoading}:${projectAllocations.length}:${
    bookedAllocation ? 'booking' : 'current'
  }`;
  const defaultAllocation =
    !currentAllocationLoading && !bookingAllocationLoading && !projectAllocations.length && bookedAllocation
      ? ALLOCATION_TYPE_BOOKING
      : ALLOCATION_TYPE_CURRENT;
  const [allocationDraft, setAllocationDraft] = useState(() => ({
    sourceKey: allocationSourceKey,
    value: defaultAllocation,
  }));
  const allocation = allocationDraft.sourceKey === allocationSourceKey ? allocationDraft.value : defaultAllocation;
  const chartData = useMemo<ChartDatum[]>(() => {
    if (!projectAllocations.length) {
      return [];
    }

    return projectAllocations.map(({ allocation, project, allocationType, area }, index) => {
      return {
        key: index,
        label: project.name || area.name || startCase(allocationType),
        value: allocation,
        color: colors[index],
      };
    });
  }, [colors, projectAllocations]);

  const onToggleChange = (newValue: string | number) => {
    setAllocationDraft({ sourceKey: allocationSourceKey, value: String(newValue) });
  };

  const isLoading = currentAllocationLoading || bookingAllocationLoading;

  return (
    <>
      <div className="dashboard__title dashboard__title--allocation">
        <div className="dashboard__title--allocation-text">
          <div>{pluralize('My Allocation', projectAllocations.length)}</div>
        </div>

        {bookedAllocation && (
          <div className="dashboard__toggle-tab">
            <PillTab
              tabs={ALLOCATION_PILL_TAB_OPTIONS}
              onTabClick={(value) => onToggleChange(value)}
              isActive={(value) => value === allocation}
            />
          </div>
        )}
      </div>
      {isLoading && (
        <SkeletonTheme baseColor="#fdfdfd" highlightColor="#f0f3f5">
          <Skeleton className="allocation-card-loader wp-100" height={196} />
        </SkeletonTheme>
      )}
      {!isLoading && allocation === ALLOCATION_TYPE_CURRENT && (
        <div className="dashboard__content flex-1 d-flex align-items-center justify-content-center">
          {isError && (
            <div className="container mt-25">
              <div className="allocation__empty">
                <p> Sorry, couldn&apos;t load the allocation list. Please try again!.</p>
              </div>
            </div>
          )}

          {!isError && (
            <div className="allocation__content">
              {chartData.length ? (
                <CommonChart
                  className="allocation__chart"
                  chartData={chartData}
                  appendLabel={{ singular: 'hr/week', plural: 'hrs/week' }}
                  colors={colors}
                  projectAllocations={projectAllocations}
                />
              ) : (
                <div className="allocation__empty">
                  <p>{EMPTY_ALLOCATION_CHART_MESSAGE}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {!isLoading && allocation !== ALLOCATION_TYPE_CURRENT && (
        <div>
          <div className="booking-allocation custom-scroll-bar px-16 ptpx-12 pbpx-12">
            <BookingAllocation bookedAllocation={bookedAllocation} />
          </div>
        </div>
      )}
    </>
  );
}

export default AllocationAndBooking;
